import React from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import SummarizeRounded from '@mui/icons-material/SummarizeRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import ForumRounded from '@mui/icons-material/ForumRounded';
import {
  AttentionList, EmptyState, EntityTile, PageHeader, Panel, SegmentedControl, type EntityTileFigure,
} from '../../ui-kit';
import { ConfirmDialog } from '../../components/dialog';
import { useCan } from '../../ui/PermissionsProvider';
import {
  deleteTenantAgent, fetchAgentsOverview, fetchTenantAgents, updateTenantAgent, type AgentPulse, type AgentsOverview,
  type TenantAgent,
} from '../../services/tenantAgentsService';
import PlaybooksListPage from '../playbooks/PlaybooksListPage';
import { EditDetailsModal, tileMenu } from './tileMenu';
import { addressOf } from './agentAddress';
import { identityPalette } from '../../theme/charts';

// Cards across the page: three on a wide screen, one on a phone.
const GRID = { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' };
// How often the figures are read again while the page is open: our own
// tables only, never a warehouse, so this costs nothing to keep current.
const REFRESH_MS = 30_000;

/**
 * A policy's journey through an agency, in order. Each agent works at one
 * stage (its kind says which), and the page groups them so. A stage with no
 * agent in this workspace is not drawn.
 *
 * Under each stage's name is what an agency gains from AI agents there,
 * said for the stage and not for one agent, so it holds whichever agents a
 * workspace has in it and whatever is added later.
 */
const STAGES: { key: string; title: string; wins: string }[] = [
  { key: 'intake', title: 'Intake', wins: 'Improve contact rate and conversions' },
  { key: 'quote', title: 'Quote', wins: 'Quote faster and compare more carriers for every client' },
  { key: 'bind_issue', title: 'Bind and issue', wins: 'Shorten time to issue and catch errors before delivery' },
  { key: 'service', title: 'Service', wins: 'Answer clients faster and free up the service team' },
];

// What the saved analyses' group gains, said the same way.
const INSIGHTS_WINS = 'See what is working without manual reporting';

type Days = '7' | '30' | '90';
const PERIODS: { value: Days; label: string }[] = [
  { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' },
];

/** "1 agent", "2 agents", "3 saved analyses". */
function countOf(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const ICONS: Record<string, React.ElementType> = {
  calls: SupportAgentRounded,
  inbox: MarkEmailReadRounded,
  whatsapp: WhatsApp,
  check: FactCheckRounded,
  report: SummarizeRounded,
  quote: RequestQuoteRounded,
  chat: ForumRounded,
};

/** A small mark in a stage's colour: the theme's categorical palette, without its reds. */
function StageDot({ slot, size = 10 }: { slot: number; size?: number }) {
  return (
    <Box component="span" sx={(t) => ({
      width: size, height: size, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
      bgcolor: identityPalette(t.tokens.chart.palette)[slot % identityPalette(t.tokens.chart.palette).length],
    })} />
  );
}

/**
 * /agents: every agent the workspace has, at work.
 *
 * Read top to bottom it answers what an agency asks of a team: does anything
 * need me, and what is each one doing. "Needs you" comes first, each item
 * opening where it can be done; then each stage of a policy's journey (intake,
 * quote, bind and issue, service) with its agents, a card each with its name,
 * what it does and its two figures. Saved analyses come last, folded.
 *
 * Agents — ROCCO and the rest — are provisioned from the backend, like manual
 * dashboards; an admin can rename, re-describe or delete one from its ⋯ menu.
 * A workspace with no provisioned agents sees exactly the Playbooks page it
 * always had.
 */
export default function AgentsListPage() {
  const [agents, setAgents] = React.useState<TenantAgent[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setAgents(await fetchTenantAgents());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load agents');
      setAgents([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // No agents here: the page is the Playbooks page, as it always was.
  if (agents !== null && agents.length === 0) return <PlaybooksListPage />;

  return <AgentsAtWork agents={agents} error={error} onChanged={load} />;
}

function AgentsAtWork({
  agents, error, onChanged,
}: { agents: TenantAgent[] | null; error: string | null; onChanged: () => void }) {
  const navigate = useNavigate();
  const canManage = useCan('manage_workspace');
  const [days, setDays] = React.useState<Days>('30');
  const [overview, setOverview] = React.useState<AgentsOverview | null>(null);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<TenantAgent | null>(null);
  const [deleting, setDeleting] = React.useState<TenantAgent | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  // The figures, read now and every half minute; a new period shimmers
  // rather than showing the last period's numbers under the new label.
  React.useEffect(() => {
    let live = true;
    setOverview(null);
    const read = async () => {
      try {
        const next = await fetchAgentsOverview(Number(days));
        if (live) { setOverview(next); setOverviewError(null); }
      } catch (err) {
        if (live) setOverviewError(err instanceof Error ? err.message : 'Could not load what the agents are doing');
      }
    };
    void read();
    const timer = window.setInterval(read, REFRESH_MS);
    return () => { live = false; window.clearInterval(timer); };
  }, [days]);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setProblem(null);
    try {
      await deleteTenantAgent(deleting.id);
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete the agent');
    } finally {
      setDeleting(null);
      setBusy(false);
    }
  };

  const all = agents ?? [];
  const pulses = new Map((overview?.agents ?? []).map((a) => [a.id, a.pulse]));
  const pathTo = (agentId: string, section: string) => {
    const agent = all.find((a) => a.id === agentId);
    return agent ? `/agents/${addressOf(agent, all)}/${section}` : null;
  };
  const go = (agentId: string, section: string) => {
    const path = pathTo(agentId, section);
    return path ? () => navigate(path) : undefined;
  };
  const loadingFigures = overview === null;

  // Each stage with the agents this workspace has in it. An agent whose kind
  // names no stage we know is still shown, in a group of its own at the end.
  // Each group keeps its place in the palette, so its colour follows it.
  const known = new Set(STAGES.map((stage) => stage.key));
  const stray = all.filter((agent) => !agent.stage || !known.has(agent.stage));
  const groups = [
    ...STAGES.map((stage, slot) => ({ ...stage, slot, agents: all.filter((agent) => agent.stage === stage.key) })),
    { key: 'other', title: 'Other agents', wins: 'Hand routine work to an agent', slot: STAGES.length, agents: stray },
  ].filter((group) => group.agents.length > 0);

  const tile = (agent: TenantAgent, slot: number) => {
    const Icon = ICONS[agent.icon || ''] || SmartToyOutlined;
    const pulse: AgentPulse | null | undefined = pulses.get(agent.id);
    const open = agent.ready ? go(agent.id, 'spotlight') : undefined;
    const waiting = (pulse?.needs ?? []).reduce((sum, need) => sum + need.count, 0);
    // Two figures: what it handled, and what waits on a person.
    const figures: EntityTileFigure[] = agent.ready && (pulse || loadingFigures) ? [
      { key: 'done', value: pulse ? pulse.done.toLocaleString() : '—', label: pulse?.done_label || 'handled' },
      {
        key: 'needs', value: pulse ? waiting.toLocaleString() : '—', label: waiting === 1 ? 'needs you' : 'need you',
        tone: waiting ? 'attention' : undefined,
      },
    ] : [];
    return (
      <EntityTile
        key={agent.id}
        title={agent.name}
        description={agent.description}
        icon={<Icon sx={{ fontSize: 20 }} />}
        accent={slot}
        // A badge only when something is different: not switched on, or stopped.
        status={!agent.ready ? { tone: 'neutral', label: 'Coming next' }
          : pulse?.problem ? { tone: 'bad', label: 'Needs attention' } : null}
        figures={figures}
        footnote={agent.ready ? null : 'Not switched on in this workspace yet'}
        onOpen={open}
        actions={tileMenu({
          onOpen: open,
          onEdit: canManage ? () => setEditing(agent) : undefined,
          onDelete: canManage ? () => setDeleting(agent) : undefined,
        })}
      />
    );
  };

  const needs = overview?.needs ?? [];
  const waitingTotal = needs.reduce((sum, need) => sum + (need.count ?? 1), 0);

  return (
    <Box>
      <PageHeader
        title="Agents"
        description="Each one does a job end to end, on your own systems. Open one to see how it is set up and everything it has done."
        actions={<SegmentedControl value={days} options={PERIODS} onChange={setDays} ariaLabel="Period" />}
      />
      {error || problem || overviewError
        ? <Box sx={{ mt: 2 }}><EmptyState message={problem || error || overviewError || ''} /></Box> : null}

      <Box sx={{ mt: 2.5 }}>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {loadingFigures || needs.length ? (
            <Panel
              title={needs.length ? `Needs you · ${waitingTotal.toLocaleString()}` : 'Needs you'}
              subtitle={loadingFigures ? 'Reading every agent…' : 'Waiting on a person, across every agent'}
            >
              <AttentionList
                loading={loadingFigures}
                items={needs.map((need, i) => ({
                  key: `${need.agent_id}-${i}`,
                  label: need.count == null ? need.label : `${need.count.toLocaleString()} ${need.label}`,
                  detail: need.agent_name,
                  tone: need.problem ? 'problem' : 'waiting',
                  onOpen: go(need.agent_id, need.section),
                }))}
              />
            </Panel>
          ) : (
            // Nothing waits: one calm line, not an empty box.
            <Box sx={(t) => ({
              px: 2, py: 1.25, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.raised,
            })}>
              <AttentionList items={[]} emptyMessage="Nothing needs you right now. Every agent is working on its own." />
            </Box>
          )}



          <Stack spacing={2.5}>
            {agents === null ? (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
                {[0, 1, 2, 3].map((i) => <EntityTile key={i} title="" loading />)}
              </Box>
            ) : groups.map((group) => (
              // A box per stage, its name and line above its cards, like Insights below.
              <Box key={group.key} id={`stage-${group.key}`} sx={{ scrollMarginTop: 72 }}>
                <Panel
                  title={(
                    <Stack direction="row" spacing={1} alignItems="center" component="span">
                      <StageDot slot={group.slot} />
                      <span>{group.title}</span>
                    </Stack>
                  )}
                  subtitle={`${group.wins} · ${countOf(group.agents.length, 'agent', 'agents')}`}
                >
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
                    {group.agents.map((agent) => tile(agent, group.slot))}
                  </Box>
                </Panel>
              </Box>
            ))}
          </Stack>

          {agents !== null ? (
            <PlaybooksListPage
              renderAsTiles={(playbookTiles, playbooksLoading) => (playbookTiles.length || playbooksLoading ? (
                <Panel
                  title="Insights"
                  subtitle={`${INSIGHTS_WINS} · ${playbooksLoading && !playbookTiles.length ? 'loading'
                    : countOf(playbookTiles.length, 'saved analysis', 'saved analyses')}`}
                  collapsible
                  defaultCollapsed
                >
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: GRID }}>
                    {playbookTiles}
                    {playbooksLoading ? <EntityTile title="" loading /> : null}
                  </Box>
                </Panel>
              ) : null)}
            />
          ) : null}
        </Stack>
      </Box>

      {editing ? (
        <EditDetailsModal
          title={editing.name}
          name={editing.name}
          description={editing.description ?? ''}
          onClose={() => setEditing(null)}
          onSave={async (change) => {
            await updateTenantAgent(editing.id, change);
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'agent'}?`}
        description={deleting?.kind === 'rocco'
          ? `${deleting.name} stops calling leads the moment it is deleted, and disappears from Agents. `
            + 'A call already under way is allowed to finish. The calls it made and the callbacks it booked are kept.'
          : `${deleting?.name ?? 'It'} disappears from Agents. Anything it has already done is kept.`}
        tone="destructive"
        confirmLabel="Delete"
        requireTypeToConfirm={deleting?.kind === 'rocco' ? deleting.name : undefined}
        onConfirm={remove}
        busy={busy}
      />
    </Box>
  );
}
